import {
  Assessment,
  AssessmentEvent,
  CreateAssessmentEventInput,
  CreateAssessmentEventMutation,
  CreateAssessmentEventMutationVariables,
  GetAssessmentQuery,
  GetAssessmentQueryVariables,
  ListAssessmentEventsQuery,
  ListAssessmentsQuery,
  ListAssessmentsQueryVariables,
  ListQuestionsQuery,
  ModelAssessmentConnection,
  ModelAssessmentEventConnection,
  ModelQuestionConnection,
  Question,
  SubmitAssessmentRequestMutation,
  SubmitAssessmentRequestMutationVariables,
  UpdateAssessmentInput,
  UpdateAssessmentMutation,
  SubmitAssessmentRequestInput,
  AssessmentRole
} from "../../API";
import { genericQuery, listQueryAutoPaginated } from "../api/apiUtils";
import * as queries from "../../graphql/queries";
import * as mutations from "../../graphql/mutations";
import { API } from "aws-amplify";

export const fetchAllAssessments = async (predicate?: object) => {
  return await listQueryAutoPaginated<
    ListAssessmentsQuery,
    Assessment,
    ModelAssessmentConnection
  >(queries.listAssessments, "listAssessments", "items", predicate);
};

export const fetchAllAssessmentEvents = async (predicate?: object) => {
  return await listQueryAutoPaginated<
    ListAssessmentEventsQuery,
    AssessmentEvent,
    ModelAssessmentEventConnection
  >(queries.listAssessmentEvents, "listAssessmentEvents", "items", predicate);
};

export const fetchAssessmentsByInstrumentId = async (id: string) => {
  const predicate: ListAssessmentsQueryVariables = {
    filter: { instrumentId: { eq: id } }
  };
  return await listQueryAutoPaginated<
    ListAssessmentsQuery,
    Assessment,
    ModelAssessmentConnection
  >(queries.listAssessments, "listAssessments", "items", predicate);
};

export const fetchAssessment = async (id: string) => {
  // Grab the assessment
  const assessmentPredicate = { id: id };
  const assessment = await genericQuery<GetAssessmentQuery, Assessment>(
    queries.getAssessment,
    "getAssessment",
    assessmentPredicate
  );

  // Need to grab the questions object because the depth setting can't grab everything from the assessment level.
  const questionsPredicate = {
    filter: { instrumentId: { eq: assessment?.instrumentId } }
  };
  const questions = await listQueryAutoPaginated<
    ListQuestionsQuery,
    Question,
    ModelQuestionConnection
  >(queries.listQuestions, "listQuestions", "items", questionsPredicate);
  // Create the expected connection type
  const questionsConnection: ModelQuestionConnection = {
    __typename: "ModelQuestionConnection",
    items: questions || []
  };
  // Update the returned assessment if there were questions returned from the secondary query
  if (assessment && assessment.instrument && questions) {
    assessment.instrument.questions = questionsConnection;
  }
  return assessment;
};

export const updateAssessment = async (input: UpdateAssessmentInput) => {
  const predicate = { input: input };
  return await genericQuery<UpdateAssessmentMutation, Assessment>(
    mutations.updateAssessment,
    "updateAssessment",
    predicate
  );
};

export const getAssessmentById = async (assessmentId: string) => {
  const predicate: GetAssessmentQueryVariables = {
    id: assessmentId
  };
  return await genericQuery<GetAssessmentQuery, Assessment>(
    queries.getAssessment,
    "getAssessment",
    predicate
  );
};

export const submitAssessmentRequest = async (
  input: SubmitAssessmentRequestInput
) => {
  const predicate: SubmitAssessmentRequestMutationVariables = {
    input: input
  };
  return await genericQuery<SubmitAssessmentRequestMutation, AssessmentEvent>(
    mutations.submitAssessmentRequest,
    "submitAssessmentRequest",
    predicate
  );
};

export const createAssessmentEvent = async (
  subjectId: string,
  subjectUserId: string,
  raterId: string,
  raterUserId: string,
  activityId: string,
  activityEventId: string,
  programId: string,
  assessmentId: string,
  expirationDate: string
) => {
  const predicate: CreateAssessmentEventInput = {
    subjectId: subjectId,
    subjectUserId: subjectUserId,
    raterId: raterId,
    raterUserId: raterUserId,
    activityId,
    activityEventId,
    programId,
    assessmentId,
    expirationDate,
    completionDate: null,
    declined: null
  };
  const input: CreateAssessmentEventMutationVariables = {
    input: predicate
  };
  return await genericQuery<CreateAssessmentEventMutation, AssessmentEvent>(
    mutations.createAssessmentEvent,
    "createAssessmentEvent",
    input
  );
};

export type AssessmentRoleResponse = {
  assessmentRole: AssessmentRole;
  showPrompt: boolean;
};

export const getAssessmentRole = async (userId?: string) => {
  if (!userId) throw new Error("userId is required");
  const path = `network-rating-rules/${encodeURIComponent(userId)}`;
  const response = (await API.get(
    "simpl-api",
    path,
    {}
  )) as AssessmentRoleResponse;
  return response;
};
